import { useCallback, useMemo, useState } from 'react';
import { randomString } from 'remeda';
import { BehaviorConfigOption } from 'src/graphql';
import {
  CustomViewFormConditionField,
  CustomViewFormConditionItem,
  UIType,
} from 'src/graphql/entities/custom-view';
import { isMulti } from 'src/graphql/entities/custom-view/custom-view-config/operators';
import { ConditionOperator } from 'src/graphql/types';

export interface ChipFilterDefinition<Attr extends string = string> {
  attribute: Attr;
  field: Omit<CustomViewFormConditionItem['field'], 'optionsQuery'> & {
    optionsQuery: string;
  };
  defaultOperator: ConditionOperator;
  operators?: readonly ConditionOperator[];
  useOptions: () => BehaviorConfigOption[];
}

export interface RawFilter<Attr extends string = string> {
  attribute: Attr;
  value: unknown;
  operator: ConditionOperator;
}

export function useChipFilters<Attr extends string>(
  defs: readonly ChipFilterDefinition<Attr>[],
) {
  const [filters, setFilters] = useState<RawFilter<Attr>[]>([]);

  /* lookup tables */
  const defByAttr = useMemo(
    () =>
      Object.fromEntries(defs.map((d) => [d.attribute, d])) as Record<
        Attr,
        ChipFilterDefinition<Attr>
      >,
    [defs],
  );

  /* options for <FilterChips /> */
  const queryOptions = useMemo(() => {
    return Object.fromEntries(
      defs.map((d) => {
        const opts = d.useOptions();
        return [d.field.optionsQuery, { options: opts, historicalOptions: opts }];
      }),
    );
  }, [defs]);

  /* raw to chip */
  const chipConditions = useMemo<CustomViewFormConditionItem[]>(() => {
    return filters.map((f) => {
      const def = defByAttr[f.attribute];
      const opts = queryOptions[def.field.optionsQuery]?.options ?? [];
      const asChipOpt = (v: unknown) => {
        const found = opts.find((o) => String(o.value) === String(v))!;
        return {
          id: found?.id ? String(found?.id) : String(v),
          value: (found?.value as string) ?? String(v),
          label: found?.label ?? String(v),
        };
      };

      const field = def.field as CustomViewFormConditionField;

      let value: CustomViewFormConditionItem['value'];

      if (field?.uiType === UIType.List) {
        value = isMulti(f.operator)
          ? (f.value as unknown[])?.map(asChipOpt)
          : [asChipOpt(f.value)];
      } else {
        value = String(f.value);
      }

      return {
        id: randomString(8),
        field,
        operator: f.operator,
        value,
      };
    });
  }, [filters, defByAttr, queryOptions]);

  const handleChipConditionsChange = useCallback((updated: CustomViewFormConditionItem[]) => {
    const next = updated.map<RawFilter<Attr>>((c) => {
      const attr = c.field!.value as Attr;
      let raw: unknown;

      if (isMulti(c.operator) && Array.isArray(c.value)) {
        raw = c.value.map((v) => (v as { value: unknown }).value);
      } else if (Array.isArray(c.value)) {
        raw = (c.value[0] as { value: unknown }).value;
      } else {
        raw = c.value;
      }

      return { attribute: attr, value: raw, operator: c.operator! };
    });
    setFilters(next);
  }, []);

  /* delete whole attribute (chips collapse multiple values anyway) */
  const deleteChipCondition = useCallback(
    (id: string) =>
      setFilters((prev) =>
        prev.filter(
          (f) => !chipConditions.find((c) => c.id === id && c.field!.value === f.attribute),
        ),
      ),
    [chipConditions],
  );

  return {
    filters,
    chipConditions,
    chipQueryOptions: queryOptions,
    handleChipConditionsChange,
    deleteChipCondition,
  };
}
